import React, { useCallback, useEffect, useState } from "react";
import MaterialReactTable, {
  MRT_BottomToolbar,
  MRT_ColumnDef,
} from "material-react-table";
import { useTheme } from "@mui/material/styles";
import { MRT_Localization_IT } from "material-react-table/locales/it";
import { MRT_Localization_EN } from "material-react-table/locales/en";
import { CustomLi, Row } from "./style";
import { TableInterface } from "./types";
import { useHeader } from "@context/header";
import { getLocalStorage, setLocalStorage } from "@utils/utilsCookie";
import CustomTopToolbar from "./components/CustomTopToolbar";
import CustomRowAction from "./components/CustomRowAction";
import {
  Box,
  capitalize,
  Checkbox,
  Tooltip,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import OptionsConfig from "../../assets/static/options.json";
import { useFilters } from "@context/filters";
import { useAuth } from "@context/auth";
import { disableRowBasedOnDisableIf } from "@utils/utilsTable";
import { useParams } from "react-router-dom";
import { UseLabelTableStyles } from "../../hooks/useLabelTableStyles";
import { IconLabel } from "../IconLabel/Index";
import i18next from "i18next";
import EuroIcon from "@mui/icons-material/Euro";

const Table: React.FC<TableInterface> = ({
  id,
  configurationTopToolbar,
  configurationActionForRow,
  totalPages = 0,
  enableMultiRowSelection,
  enableRowSelection,
  enableRowDrag,
  onRowSelectionChange,
  onDragChange,
  selectedRows,
  modalTitle,
  renamedActionsColumn,
  noSummarySelected,
  numberOfActions,
  isActivities,
  renderCell,
  setActionData,
  renderActionIcon,
  renderActionIconBottomToolbar,
  renderActionUnderBottomToolbar,
  hiddenFilters = false,
  resetFormFilters = "",
  enableSelectAll = false,
  filtersException,
  enableFullScreenToggle = true,
  modAssociationEnabled = false,
  clientSidePagination = false,
  renderBelowFilters,
  testIdPrefix,
  ...props
}) => {
  // const defaultColumnOrder = columns.map(({ accessorKey }) => accessorKey);
  const {
    columns,
    data,
    enableRowActions = true,
    renderRowActions,
    renderTopToolbar,
  } = props;

  const { user } = useAuth();

  const params = useParams();
  const currentLanguage = i18next.language;
  const theme = useTheme();
  const empty = {
    accessorKey: "emptyData",
    header: "",
    visibility: true,
    text: {
      it: "Nessun dato presente",
      en: "No data to show",
    },
  };

  const switchLang = (lang: string) => {
    switch (currentLanguage) {
      case "en":
        return MRT_Localization_EN;
      case "it":
        return MRT_Localization_IT;
    }
  };

  const defaultPinning = useCallback(() => {
    // mrt-row-select
    let defaultVisibility: { left: string[]; right: string[] } = {
      left: [],
      right: [],
    };
    enableRowSelection && defaultVisibility?.left?.unshift("mrt-row-select");
    return defaultVisibility;
  }, [enableRowSelection]);

  const defaultVisibility = (columns: MRT_ColumnDef<any>[]) => {
    let defaultVisibility = {};
    defaultVisibility = columns.reduce((tot: any, elem: any) => {
      // if exist a visibility param, the column is displayed accorging to the visibility params
      // else all the column is visible
      if (
        elem &&
        Object.keys(elem)?.length > 0 &&
        elem.hasOwnProperty("visibility")
      ) {
        tot = { ...tot, [elem.accessorKey]: elem.visibility };
        return tot;
      } else {
        tot = { ...tot, [elem.accessorKey]: true };
        return tot;
      }
    }, defaultVisibility);
    return defaultVisibility;
  };

  const defaultOrder = useCallback(
    (columns: MRT_ColumnDef<any>[]) => {
      const optionalColumns = ["mrt-row-expand"];
      enableRowActions && optionalColumns.unshift("mrt-row-actions");
      enableRowSelection && optionalColumns.unshift("mrt-row-select");
      enableRowDrag && optionalColumns.unshift("mrt-row-drag");
      return [
        ...optionalColumns,
        ...columns.map(({ accessorKey }) => accessorKey),
      ];
    },
    [enableRowActions, enableRowDrag, enableRowSelection],
  );

  const [selectedInternalRows, setSelectedInternalRows] = useState<any>(
    selectedRows || undefined,
  );
  const themeTable = useTheme();
  const { setHideHeader } = useHeader();
  const [columnPinning, setColumnPinning] = useState<any | undefined>(
    undefined,
  );
  const [columnVisibility, setColumnVisibility] = useState<any | undefined>(
    undefined,
  );
  const [columnOrder, setColumnOrder] = useState<any | undefined>(undefined);
  const { filters, setInternalFilters } = useFilters();
  const [toolbarHeight, setToolbarHeight] = useState(40);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const { t: tableTranslate } = useTranslation("table");
  const { t: optionsTranslate, i18n } = useTranslation("input");
  const filterId = !["", null].includes(id) ? `${user?.id_user}_${id}` : "";

  useEffect(() => {
    const mrtLocalization = switchLang(currentLanguage);
    const labelToSuffix: Record<string, string> = {
      [tableTranslate("pin-left") || ""]: "pin-left",
      [tableTranslate("pin-right") || ""]: "pin-right",
      [tableTranslate("unpin") || ""]: "unpin",
      [mrtLocalization?.showAllColumns || ""]: "show-all-columns",
      [mrtLocalization?.resetOrder || ""]: "order-reset",
      [tableTranslate("hide-all") || ""]: "hide-all",
      [mrtLocalization?.hideAll || ""]: "hide-all",
      [mrtLocalization?.unpinAll || ""]: "unpin-all",
      [mrtLocalization?.showAll || ""]: "show-all",
    };

    const hideColumnPrefix = (tableTranslate("hide-column") || "")
      .replace("{column}", "")
      .trim();

    const ariaForSuffix: Record<string, string> = {
      "pin-left": tableTranslate("testAutomation.pinLeftAria") || "",
      "pin-right": tableTranslate("testAutomation.pinRightAria") || "",
      unpin: tableTranslate("testAutomation.unpinAria") || "",
      "hide-column": tableTranslate("testAutomation.hideColumnAria") || "",
      "show-all-columns":
        tableTranslate("testAutomation.showAllColumnsAria") || "",
      "order-reset": tableTranslate("testAutomation.orderResetAria") || "",
      "hide-all": tableTranslate("testAutomation.hideAllAria") || "",
      "unpin-all": tableTranslate("testAutomation.unpinAllAria") || "",
      "show-all": tableTranslate("testAutomation.showAllAria") || "",
    };

    const tagItem = (item: Element) => {
      if (item.getAttribute("data-testid")?.startsWith("table-column-menu-"))
        return;
      const text = (item.textContent || "").trim();
      let suffix = labelToSuffix[text];
      if (!suffix && hideColumnPrefix && text.startsWith(hideColumnPrefix)) {
        suffix = "hide-column";
      }
      if (!suffix) return;
      item.setAttribute("id", `table-column-menu-${suffix}`);
      item.setAttribute("data-testid", `table-column-menu-${suffix}`);
      const aria = ariaForSuffix[suffix];
      if (aria) item.setAttribute("aria-label", aria);
    };

    const tagOpenMenus = () => {
      document
        .querySelectorAll(
          '[role="menu"] [role="menuitem"], [role="menu"] button',
        )
        .forEach(tagItem);
    };

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of Array.from(mutation.addedNodes)) {
          if (!(node instanceof HTMLElement)) continue;
          if (
            node.matches?.('[role="menu"]') ||
            node.querySelector?.('[role="menu"]')
          ) {
            tagOpenMenus();
            return;
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    tagOpenMenus();

    return () => observer.disconnect();
  }, [tableTranslate, i18n.language]);

  useEffect(() => {
    if (selectedInternalRows !== undefined) {
      onRowSelectionChange && onRowSelectionChange(selectedInternalRows);
    }
  }, [selectedInternalRows]);

  useEffect(() => {
    if (JSON.stringify(selectedRows) !== JSON.stringify(selectedInternalRows)) {
      setSelectedInternalRows(selectedRows);
    }
  }, [selectedRows]);

  const additionalTraduction = {
    hideAll: tableTranslate("hide-all"),
    sortByColumnAsc: tableTranslate("sort-col-asc"),
    sortByColumnDesc: tableTranslate("sort-col-desc"),
    filterByColumn: tableTranslate("filter-col"),
    pinToLeft: tableTranslate("pin-left"),
    pinToRight: tableTranslate("pin-right"),
    unpin: tableTranslate("unpin"),
    hideColumn: tableTranslate("hide-column"),
    actions: renderActionIcon
      ? renderActionIcon
      : tableTranslate(renamedActionsColumn || "actions"),
  };

  useEffect(() => {
    if (columns.length > 0) {
      const configTable = getLocalStorage(filterId);
      const config: { [x: string]: any } = configTable
        ? JSON.parse(configTable)
        : undefined;
      if (configTable !== "" && modAssociationEnabled === false) {
        if (JSON.stringify(config.columns) !== JSON.stringify(columns)) {
          const diffColumns: any = columns.map((col) => {
            const findColumn = Object.keys(config.columnVisibility).find(
              (el) => el === col.accessorKey,
            );
            if (!!findColumn) {
              return {
                ...col,
                visibility: config.columnVisibility[findColumn],
              };
            }
            return col;
          });
          const defVisibility = defaultVisibility(diffColumns);
          const defOrder = defaultOrder(diffColumns);
          setColumnVisibility(defVisibility);
          setColumnOrder(defOrder);
        } else {
          setColumnVisibility(config.columnVisibility);
          setColumnOrder(config.columnOrder);
        }
        setColumnPinning(config.columnPinning);
      } else {
        const defPinning = defaultPinning();
        const defVisibility = defaultVisibility(
          columns as MRT_ColumnDef<any>[],
        );
        const defOrder = defaultOrder(columns as MRT_ColumnDef<any>[]);
        setColumnVisibility(defVisibility);
        setColumnOrder(defOrder);
        setColumnPinning(defPinning);
      }
    }
  }, [id, defaultOrder, columns, defaultPinning]);

  //PERCHE QUESTA FUNZIONE????
  const updatePagination = (filters: any) => {
    const { pageIndex, ...restFilters } = filters;

    if (pageIndex !== undefined && restFilters["81050_corporate"]) {
      return {
        ...restFilters,
        "81050_corporate": {
          ...restFilters["81050_corporate"],
          pageIndex,
        },
      };
    }

    return filters;
  };

  const handlePaginationChange = (updater: any) => {
    setInternalFilters((oldPagination: any) => {
      const newPagination =
        typeof updater === "function" ? updater(oldPagination) : updater;

      /*
          // PAGE NUMBER
          let newPageIndex = 0
          if(!newPagination.pageIndex && !newPagination?.filters[filterId]?.pageIndex){
            newPageIndex = 0
          }else if(!newPagination.pageIndex && newPagination?.filters[filterId]?.pageIndex){
            newPageIndex = filters[filterId]?.pageIndex
          }else{
            newPageIndex = newPagination.pageIndex
          }
          // SIZE ROW
          let newPageSize = 20
          if(!newPagination.pageSize && !newPagination?.filters[filterId]?.pageSize){
            newPageIndex = 20
          }else if(!newPagination.pageSize && newPagination?.filters[filterId]?.pageSize){
            newPageIndex = filters[filterId]?.pageSize
          }else{
            newPageIndex = newPagination?.pageSize
          }
        console.log({newPagination,filters})
        // MERGE NEW PAGE AND ROW SIZE INSIDE FILTERS CONTEXT
*/
      const newPageIndex =
        newPagination.pageIndex || newPagination.pageIndex === 0
          ? newPagination.pageIndex
          : oldPagination[filterId]?.pageIndex || 0;
      const newPageSize = newPagination.pageSize
        ? newPagination.pageSize
        : oldPagination[filterId]?.pageSize || 20;

      const newfiltersPage = {
        [filterId]: {
          ...oldPagination[filterId],
          ...{ pageIndex: newPageIndex, pageSize: newPageSize },
        },
      };
      return { ...filters, ...newfiltersPage };
    });
  };

  useEffect(() => {
    if (columnOrder || columnPinning || columnVisibility) {
      const newConfig = {
        columnPinning,
        columnVisibility,
        columnOrder,
        columns,
      };
      //OLD LOGIC INSIDE IF STATEMENT =>   !["", null].includes(filterId)
      if (modAssociationEnabled === false) {
        setLocalStorage(filterId, newConfig, 1);
      }
    }
  }, [columnPinning, columnVisibility, columnOrder, filterId, columns]);

  const formatColumnsFilter = useCallback(() => {
    const columnsOrdered: any[] = [];
    let i = 0;
    columnOrder &&
      columnOrder.forEach((column: any) => {
        if (columns.some((elem) => elem.accessorKey === column)) {
          const foundedIndex = columns.findIndex(
            (elem) => elem.accessorKey === column,
          );
          columnsOrdered[i] = columns[foundedIndex];
          i = i + 1;
        }
      });
    return [
      ...columnsOrdered.filter((column) =>
        columnVisibility !== undefined &&
        columnVisibility &&
        columnVisibility[column.accessorKey] === false
          ? false
          : true,
      ),
    ].filter((col) => col.hasFilter);
  }, [columnOrder, columnVisibility, columns]);

  const customRenderBottomToolbar = ({
    table,
  }: {
    table: any;
  }): React.ReactNode => {
    if (renderActionIconBottomToolbar) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            alignContent: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", paddingInlineStart: "1rem" }}>
            {renderActionIconBottomToolbar}
          </Box>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
            }}
          >
            <Box sx={{ width: "100%" }}>
              <MRT_BottomToolbar table={table} />
            </Box>
            {renderActionUnderBottomToolbar && (
              <Box sx={{ padding: "1rem" }}>
                {renderActionUnderBottomToolbar}
              </Box>
            )}
          </Box>
        </Box>
      );
    }

    return <MRT_BottomToolbar table={table} />;
  };
  const customRenderTopToolbar = ({
    table,
  }: {
    table: any;
  }): React.ReactNode => {
    let formattedColumns = formatColumnsFilter();
    return (
      <CustomTopToolbar
        id={id}
        key={`custom_top_toolbar_${id}`}
        columns={formattedColumns}
        configurationTopToolbar={configurationTopToolbar}
        table={table}
        hiddenFilters={hiddenFilters}
        resetFormFilters={resetFormFilters}
        setHideHeader={setHideHeader}
        counterElements={!!enableRowSelection}
        onToolbarChange={(height) => setToolbarHeight(height)}
        modalTitle={modalTitle}
        noSummarySelected={noSummarySelected}
        filtersException={filtersException}
        enableFullScreenToggle={enableFullScreenToggle}
        renderBelowFilters={renderBelowFilters}
        testIdPrefix={testIdPrefix}
      />
    );
  };

  const customRenderRowActions = ({ row }: { row: any }): React.ReactNode => {
    let configRowNew: any = configurationActionForRow && [
      ...configurationActionForRow,
    ];
    // Check if inside action's config is present disableIf and check cases passed inside it
    configRowNew = disableRowBasedOnDisableIf(configRowNew, row, {
      corporateID: params?.id,
    });
    /* if (configRowNew && configRowNew?.find((el:any) => el.type === "popper")) {
      if (
        row.original.publish_date &&
        dayjs(row.original.publish_date).isAfter(dayjs())
      ) {
        configRowNew =
          configRowNew &&
          configRowNew?.map((el:any) => {
            switch (el.key) {
              case "enrollment":
                return { ...el, disabled: true };
              case "enrollment-editions":
                return { ...el, disabled: true };
              case "subscribed":
                return { ...el, disabled: true };
              case "unsubscribed":
                return { ...el, disabled: true };
              default:
                return el;
            }
          });
      }
    } */

    return (
      enableRowActions && [
        <CustomRowAction
          key={row.id}
          row={row}
          configurationActionForRow={configRowNew}
          setHideHeader={setHideHeader}
          setActionData={setActionData}
        />,
      ]
    );
  };

  const renderColumnActionsMenuItems = () => [<CustomLi></CustomLi>];

  const cellRender = (cel: any, value: any, row: any) => {
    const { type, optionsKey } = cel;
    switch (type) {
      // case "time": {
      //   const date = dayjs(new Date(value)?.toLocaleString()).format(
      //     "DD/MM/YYYY HH:mm",
      //   );
      //   return value ? <Row>{date || "-"}</Row> : "-";
      // }
      case "dateTime": {
        const date =
          new Date(value)?.toLocaleString()?.split(",") &&
          new Date(value)?.toLocaleString()?.split(",")[0];
        return value ? <Row>{date || "-"}</Row> : "-";
      }
      case "dateTimeYear": {
        return <Row>{value}</Row>;
      }
      case "boolean": {
        // cel.optionsKey === "yes-no"
        return <Checkbox disabled checked={value as boolean} />;
      }
      case "select-checkbox":
      case "multiCheckbox": {
        if (
          Array.isArray(value) &&
          value.find((obj: any) => typeof obj === "object")
        ) {
          const allValues = value
            .map((val: any) => {
              if (typeof val === "string") {
                return val;
              }
              return val.title || val.label || val.id || "-";
            })
            .join(", ");

          return (
            <Tooltip title={allValues}>
              <Row>{allValues}</Row>
            </Tooltip>
          );
        }
        const mapping = Array.isArray(value) ? value?.join(", ") : value;
        return <Row>{mapping || "-"}</Row>;
      }
      case "chips": {
        const mapping = value?.join(", ");
        return <Row>{mapping || "-"}</Row>;
      }
      case "dynamicSelect":
      case "select": {
        const val = value;
        if ([null, undefined].includes(value)) {
          return <Row>-</Row>;
        }
        if (val?.label || val?.code || val?.name) {
          return val.label || val?.code || val?.name;
        } else {
          let options =
            OptionsConfig.find((option) => {
              return option.key === optionsKey;
            })?.options || [];
          if (options) {
            if (
              optionsKey === "LearningObjectType" &&
              !options.includes(value.toString().toUpperCase())
            ) {
              value = "else";
            }

            let translateLabel: string;
            const rawValue = Array.isArray(value)
              ? value.join(",")
              : String(value);
            if (rawValue.includes(",")) {
              const parts = rawValue
                .split(",")
                .map((p) => p.trim())
                .filter((p) => p !== "");
              const translatedParts = parts.map((part) => {
                const key = `options.${optionsKey}.${part.toLowerCase()}`;
                const tr = optionsTranslate(key);
                return tr && tr !== key ? tr : part;
              });
              translateLabel = translatedParts.join(", ");
            } else {
              translateLabel = optionsTranslate(
                `options.${optionsKey}.${rawValue.toLowerCase()}`,
              );
            }
            if (
              optionsKey === "lo_types" &&
              !["COURSE", "PATH", "ACTIVITY"].includes(value)
            ) {
              translateLabel = optionsTranslate(
                `options.activity_type.${value.toString().toLowerCase()}`,
              );
            }
            return cel.dynamicColorData ? (
              <Row style={UseLabelTableStyles(cel.dynamicColorData, val)}>
                {translateLabel}
              </Row>
            ) : (
              <Row>{translateLabel}</Row>
            );
          } else {
            return (
              <Row>
                {value
                  ? (value as { [key: string]: string })[i18n.language]
                  : "-"}
              </Row>
            );
          }
        }
      }
      case "association-with-id": {
        if (Array.isArray(value)) {
          const formattedValue = value
            .map((val: any) => {
              if (typeof val === "string") {
                return val;
              }
              return val.id || "-";
            })
            .join(", ");

          return (
            <Tooltip title={formattedValue}>
              <Row>{formattedValue}</Row>
            </Tooltip>
          );
        } else if (value && Object.keys(value)?.length > 0) {
          return (
            <Tooltip title={value?.id || "-"}>
              <Row>{value?.id || "-"}</Row>
            </Tooltip>
          );
        }
        break;
      }
      case "association_users": {
        if (Array.isArray(value)) {
          const formattedValue = value
            .map((val: any) => {
              return val.name && val.surname
                ? `${val.name} ${val.surname}`
                : val.externalId;
            })
            .join(", ");
          return (
            <Tooltip title={formattedValue}>
              <Row>{formattedValue}</Row>
            </Tooltip>
          );
        }
        break;
      }
      case "association":
      case "select_with_association": {
        if (Array.isArray(value)) {
          const formattedValue = value
            .map((val: any) => {
              if (typeof val === "string") {
                return val;
              }
              return val.label || val.id || "-";
            })
            .join(", ");

          return (
            <Tooltip title={formattedValue}>
              <Row>{formattedValue}</Row>
            </Tooltip>
          );
        } else if (value && Object.keys(value)?.length > 0) {
          const allValue: any[] = Object.keys(value).map(
            (key: string) => value[key],
          );
          return (
            <Tooltip title={value?.title || value?.label || value?.id || "-"}>
              <Row>{value?.title || value?.label || value?.id || "-"}</Row>
            </Tooltip>
          );
        }
        break;
      }
      case "description":
        if (Array.isArray(value)) {
          const formattedValue = value
            .map((val: any) => {
              if (typeof val === "string") {
                return val;
              }
              return val.label || val.id || "-";
            })
            .join(", ");

          return (
            <Tooltip title={formattedValue}>
              <Row>{formattedValue || "-"}</Row>
            </Tooltip>
          );
        }
        break;
      case "multi-text": {
        if (cel.accessorKey === "uog") {
          let valueUOG = value.map((el: any) => el.uog);
          return <Row>{valueUOG.length > 0 ? valueUOG : "-"}</Row>;
        }
        break;
      }
      case "userInfo": {
        return value ? (
          <Row>
            {value?.id + " - " + value?.firstName + " " + value?.lastName}
          </Row>
        ) : (
          "-"
        );
      }
      case "switch":
      case "selectRow": {
        if (!renderCell) return "";
        return renderCell(cel, value, row);
      }
      case "iconText": {
        return (
          <IconLabel
            row={row.original}
            icon={cel.dynamicIcon}
            value={
              value
                ? Array.isArray(value)
                  ? value.length > 0
                    ? value.join(", ")
                    : " - "
                  : value
                : " - "
            }
          />
        );
      }
      case "euro-text": {
        return (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ width: "100%" }}
          >
            <EuroIcon fontSize="small" />
            <Typography variant="body2" sx={{ flex: 1, textAlign: "right" }}>
              {value !== undefined && value !== null && value !== ""
                ? new Intl.NumberFormat("it-IT", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(Number(value))
                : "-"}
            </Typography>
          </Stack>
        );
      }
      default:
        if (typeof value === "object" && value !== null) {
          return value.name || "";
        }
        if (
          Array.isArray(value) &&
          value.find((obj: any) => typeof obj === "object")
        ) {
          const allValues = value
            .map((val: any) => {
              if (typeof val === "string") {
                return val;
              }
              return val.title || val.label || val.id || "-";
            })
            .join(", ");

          return (
            <Tooltip title={allValues}>
              <Row>{allValues}</Row>
            </Tooltip>
          );
        } else {
          return cel.dynamicColorData ? (
            <Tooltip title={value}>
              <Row>
                {value
                  ? Array.isArray(value)
                    ? value.length > 0
                      ? value.join(", ")
                      : " - "
                    : capitalize(value)
                  : " - "}
              </Row>
            </Tooltip>
          ) : (
            <Tooltip title={value}>
              <Row>
                {value
                  ? Array.isArray(value)
                    ? value.length > 0
                      ? value.join(", ")
                      : " - "
                    : value
                  : " - "}
              </Row>
            </Tooltip>
          );
        }
    }
  };

  // Normalize column pinning putting action rows column, if pinned, always
  // in the last position of the Array so the Table will render it as last
  // item and it will not change width
  const normalizeColumnPinning = () => {
    if (
      columnPinning?.left.length > 1 &&
      columnPinning?.left.includes("mrt-row-actions")
    ) {
      const newLeftPinnig = columnPinning.left.filter(
        (element: string) => element !== "mrt-row-actions",
      );
      newLeftPinnig.push("mrt-row-actions");
      columnPinning.left = newLeftPinnig;
      return columnPinning;
    }

    return columnPinning;
  };

  const getSizeColumns = () => {
    let sizeActionsColumn = 0;
    if (configurationActionForRow && configurationActionForRow?.length > 0) {
      sizeActionsColumn = configurationActionForRow?.length * 40;
    } else {
      if (numberOfActions) sizeActionsColumn = numberOfActions * 40;
    }
    return sizeActionsColumn > 120
      ? sizeActionsColumn
      : renderActionIcon
        ? 200
        : 120;
  };

  const handleFullScreenStatus = (status: any) => {
    setIsFullScreen(status);
    setHideHeader && setHideHeader(status);
  };

  const columnAttrs = (
    column: any,
    opts: { idSuffix?: string; testIdSuffix: string; ariaKey: string },
  ) => {
    const colId = `${column?.id ?? ""}`;
    if (!testIdPrefix || !colId || colId.startsWith("mrt-")) {
      return undefined;
    }
    return {
      id: `${testIdPrefix}-column-${colId}${
        opts.idSuffix ? `-${opts.idSuffix}` : ""
      }`,
      "data-testid": `${testIdPrefix}-column-${opts.testIdSuffix}`,
      "aria-label": tableTranslate(opts.ariaKey, {
        name: column?.columnDef?.header || colId,
      }),
    };
  };

  return (
    <MaterialReactTable
      localization={{
        ...switchLang(currentLanguage),
        ...additionalTraduction,
      }}
      enableRowOrdering={enableRowDrag}
      muiTableBodyRowDragHandleProps={({ table }) => ({
        onDragEnd: () => {
          const { draggingRow, hoveredRow } = table.getState();
          const dragClone: any = JSON.parse(JSON.stringify(draggingRow));
          const hovClone: any = JSON.parse(JSON.stringify(hoveredRow));
          const draggingElements = dragClone && dragClone.original;
          const hoveredElements = hovClone && hovClone.original;
          if (hoveredRow && draggingRow) {
            onDragChange && onDragChange(draggingElements, hoveredElements);
          }
        },
      })}
      enablePinning
      enableSelectAll={enableSelectAll}
      enableMultiRowSelection={enableMultiRowSelection}
      enableRowSelection={enableRowSelection}
      onRowSelectionChange={setSelectedInternalRows}
      enableColumnFilters={true}
      enableColumnResizing={true}
      enableTopToolbar={true}
      // defaultColumn={{ minSize: getSizeColumns() }}
      displayColumnDefOptions={{
        "mrt-row-actions": { size: getSizeColumns() },
        "mrt-row-select": { size: 120 },
      }}
      enableColumnOrdering={true}
      onColumnPinningChange={setColumnPinning}
      onColumnVisibilityChange={setColumnVisibility}
      onColumnOrderChange={setColumnOrder}
      {...(!clientSidePagination && {
        onPaginationChange: handlePaginationChange,
        rowCount: totalPages,
        manualPagination: true,
      })}
      // initialState={{ pagination }}
      state={{
        ...(!clientSidePagination && {
          pagination: {
            pageIndex: filters[filterId]?.pageIndex || 0,
            pageSize: filters[filterId]?.pageSize || 20,
          },
        }),
        isLoading: !data,
        ...{ columnPinning: columnPinning ? normalizeColumnPinning() : {} },
        ...{ columnVisibility: columnVisibility || [] },
        ...{ columnOrder: columnOrder },
        rowSelection: selectedRows || {},
        showProgressBars: !data,
        showGlobalFilter: true,
        isFullScreen,
      }}
      renderColumnActionsMenuItems={renderColumnActionsMenuItems}
      enableDensityToggle={false}
      enableRowActions={enableRowActions}
      onIsFullScreenChange={(status) => handleFullScreenStatus(status)}
      muiTableContainerProps={() => ({
        sx: {
          height: isFullScreen
            ? `calc(100vh - 56px - ${
                toolbarHeight < 550 ? toolbarHeight : 550
              }px)`
            : "unset",
          ".MuiTable-root": {
            opacity: columns.length > 0 ? 1 : 0,
            transition: "opacity 0.8s",
            ".MuiTableHead-root": {
              display: data?.length > 0 ? "table-row-group" : "none",
            },
            ".MuiTableBody-root": {
              tr: {
                "td:not(:last-child)": {
                  display: data?.length > 0 ? "table-cell" : "none",
                },
              },
            },
          },
        },
      })}
      renderRowActions={
        configurationActionForRow && columns?.length > 0 && data?.length > 0
          ? customRenderRowActions
          : renderRowActions
      }
      renderBottomToolbar={customRenderBottomToolbar}
      renderTopToolbar={
        configurationTopToolbar ? customRenderTopToolbar : renderTopToolbar
      }
      muiTableHeadCellProps={({ column }) => {
        const base = {
          sx: {
            ".Mui-TableHeadCell-Content-Wrapper": {
              whiteSpace: "nowrap",
              textTransform: "capitalize",
            },
          },
        };
        const attrs = columnAttrs(column, {
          testIdSuffix: "header",
          ariaKey: "testAutomation.columnHeaderAria",
        });
        return (attrs ? { ...base, ...attrs } : base) as any;
      }}
      muiBottomToolbarProps={() => ({
        sx: {
          ".MuiToolbar-root button": {
            color: themeTable.table?.actionColor,
          },
          ".MuiToolbar-root svg": { color: themeTable.table?.actionColor },
        },
      })}
      muiTableHeadCellColumnActionsButtonProps={({ column }) => {
        const base = {
          sx: {
            color: themeTable.table?.actionColor,
          },
        };
        const attrs = columnAttrs(column, {
          idSuffix: "menu",
          testIdSuffix: "menu-open",
          ariaKey: "testAutomation.columnMenuAria",
        });
        return (attrs ? { ...base, ...attrs } : base) as any;
      }}
      muiTableHeadCellDragHandleProps={({ column }) => {
        const attrs = columnAttrs(column, {
          idSuffix: "drag",
          testIdSuffix: "drag",
          ariaKey: "testAutomation.columnDragAria",
        });
        return (attrs || {}) as any;
      }}
      muiToolbarAlertBannerProps={({ table }) => ({
        sx: {
          color: themeTable.table?.actionColor,
        },
      })}
      {...props}
      columns={
        data?.length > 0
          ? columns.map((cel: any) => {
              return {
                ...cel,
                Cell: (obj: any) => {
                  const { cell, row } = obj;
                  if (cel.Cell) {
                    return <Row>{cel.Cell(obj)}</Row>;
                  }
                  return cellRender(cel || "", cell.getValue(), row);
                },
              };
            })
          : [empty]
      }
      data={
        data?.length > 0
          ? data
          : [{ emptyData: (empty.text as any)[i18n.language] }]
      }
    />
  );
};

export default Table;
